'use client';

import { LoadingButton } from '@/components/common/LoadingButton';
import { type ProductSimple } from '@/domain/products/types';
import { addFilter, clearFilters, removeFilter, setFilter, useSolutionStore } from '@/features/products/store/solution';
import {
  type Filter,
  type FilterChild,
  FilterGroup,
  type FilterItem,
  type FilterList,
  type Filters,
  type Option,
  ShowType,
} from '@/features/products/types';
import pdf from '@/services/pdf';
import { capitalizeFirstLetter } from '@/utils/string';
import { Tooltip } from '@andritzot/metris-web-ui/data-display/tooltip';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Checkbox } from '@andritzot/metris-web-ui/inputs/checkbox';
import { Radio } from '@andritzot/metris-web-ui/inputs/radio';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import { Accordion, AccordionDetails, AccordionSummary } from '@andritzot/metris-web-ui/surfaces/accordion';
import { DeleteSweep } from '@mui/icons-material';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import ShowTypeSelection from './ShowTypeSelection';

function getFilterGroupValues(filterList: FilterList, filterGroup: FilterGroup): FilterItem[] {
  switch (filterGroup) {
    case FilterGroup.BENEFIT_GROUP:
      return filterList.benefits;
    case FilterGroup.COUNTRY:
      return filterList.countries.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.INDUSTRY:
      return filterList.industries.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.INDUSTRY_AREA:
      return filterList.industries.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.PLANT_LIFECYCLE:
      return filterList.plantLifecycleManagement.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.PLANT_LIFECYCLE_GROUP:
      return filterList.plantLifecycleManagement.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.PLATFORM:
      return filterList.platform;
    case FilterGroup.PRODUCT_LINE:
      return filterList.groups.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.PRODUCT_GROUP:
      return filterList.groups.reduce<FilterItem[]>((a, b) => a.concat(b.values), []);
    case FilterGroup.REGION:
      return filterList.countries.map(({ id, group }) => ({ id, value: group }));
    case FilterGroup.USER_PERSONA:
      return filterList.userPersonas;
    case FilterGroup.PORTFOLIO:
      return filterList.portfolios;
    case FilterGroup.PRODUCT_UNIT:
      return filterList.productUnit;
    default:
      return [];
  }
}
interface ProductFilterProps {
  filteredProducts: ProductSimple[];
  filtersValue: Filters[];
  filterList: FilterList;
  isLoggedIn: boolean;
}

const ProductFilter = ({ filteredProducts, filterList, filtersValue, isLoggedIn }: ProductFilterProps) => {
  const showType = useSolutionStore((s) => s.showType);
  const industry = useSolutionStore((s) => s.industry);
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const filters = useSolutionStore((s) => s.filters);

  const filterExists = useCallback(
    (id: number, group: FilterGroup) => {
      return filters.some((f) => f.id === id && f.group === group);
    },
    [filters],
  );

  const toggleFilter = useCallback(
    (
      id: number,
      name: string,
      group: FilterGroup,
      parentId: number | null = null,
      parentGroup: FilterGroup | null = null,
    ) => {
      if (filterExists(id, group)) {
        removeFilter({ id, group });
        if (parentId && parentGroup) {
          removeFilter({ id: parentId, group: parentGroup });
        }
      } else {
        addFilter({ id, name, group });
      }
    },
    [filterExists],
  );

  const toggleParentFilter = useCallback(
    (node: FilterChild, group: FilterGroup) => {
      const currentFilters = useSolutionStore.getState().filters;

      if (filterExists(node.id, group)) {
        const childIds = new Set(node.values.map((v) => v.id));
        const newFilters = currentFilters.filter(
          (f) => !(f.id === node.id && f.group === group) && !(childIds.has(f.id) && f.group === node.group),
        );
        useSolutionStore.setState({ filters: newFilters });
      } else {
        const existingIds = new Set(currentFilters.filter((f) => f.group === node.group).map((f) => f.id));
        const newEntries: Filter[] = [
          { id: node.id, name: node.label, group },
          ...node.values
            .filter(({ id }) => !existingIds.has(id))
            .map(({ id, value }) => ({ id, name: value, group: node.group })),
        ];
        useSolutionStore.setState({ filters: [...currentFilters, ...newEntries] });
      }
    },
    [filterExists],
  );

  useEffect(() => {
    const queryFilters = searchParams.getAll('q');
    if (queryFilters.length && !filters.length && filterList) {
      const queryObject: Filter[] = [];
      queryFilters.forEach((filter) => {
        const [idString, groupString] = filter.split(';');
        const id = Number.parseInt(idString, 10);
        const group: FilterGroup = Number.parseInt(groupString, 10);

        const values = getFilterGroupValues(filterList, group);

        const item = values.find((value) => value.id === id);
        if (item) {
          queryObject.push({ id, group, name: item.value } satisfies Filter);
        }
      });
      if (queryObject.length) {
        router.push('/');
        useSolutionStore.setState({ filters: queryObject });
      }
    }
  }, [filterList, filters.length, router, searchParams]);

  const downloadSaleableProducts = useCallback(async () => {
    setIsLoadingExport(true);
    const ids = filteredProducts.filter((p) => p.saleable).map(({ idProduct }) => idProduct);

    if (!ids) {
      toast.warning('No saleable products available for given portfolio');
      return;
    }
    try {
      const portf = Object.values(filters).find((f) => f.group === FilterGroup.PORTFOLIO);
      const title = portf?.name ?? 'Saleable Products';
      await pdf.downloadPDFAsync('/pdf/saleable-solutions', title, { title, ids });
      toast.success('PDF downloaded successfully!');
    } catch {
      toast.error('Error downloading PDF!');
    } finally {
      setIsLoadingExport(false);
    }
  }, [filteredProducts, filters]);

  const renderCheckboxes = (
    { values, group }: Filters,
    parentGroup: FilterGroup | null = null,
    parentId: number | null = null,
  ) => (
    <Stack spacing={4}>
      {values.map(({ id, value, tooltip }) => {
        const tooltipId = `${id}_${value.split(' ')[0]}`;
        return (
          value && (
            <Tooltip key={value} title={tooltip ?? ''} placement="right">
              <Box sx={{ display: 'flex', alignItems: 'center' }} id={tooltipId}>
                <Checkbox
                  size="small"
                  id={value}
                  checked={filterExists(id, group)}
                  onChange={() => toggleFilter(id, value, group, parentId, parentGroup)}
                  sx={{ py: 0 }}
                />
                <Box component="label" htmlFor={value} sx={{ lineHeight: 1.25, cursor: 'pointer' }}>
                  {capitalizeFirstLetter(value)}
                </Box>
              </Box>
            </Tooltip>
          )
        );
      })}
    </Stack>
  );

  const renderCheckboxGroup = ({ group, children }: Filters) => (
    <Stack spacing={4}>
      {children?.map((node, index) => {
        const hasChildren = !!node.values.length;
        return (
          <Tooltip key={node.label} title={node.label} placement="right">
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }} id={`g${group}-p-${index}`}>
                <Checkbox
                  size="small"
                  id={node.label}
                  checked={filterExists(node.id, group)}
                  onChange={() => {
                    if (hasChildren) {
                      toggleParentFilter(node, group);
                    } else {
                      toggleFilter(node.id, node.label, node.group);
                    }
                  }}
                  sx={{ py: 0 }}
                />
                <Box component="label" htmlFor={node.label} sx={{ cursor: 'pointer' }}>
                  {node.label}
                </Box>
              </Box>
              <Box sx={{ ml: 4 }}>{renderCheckboxes(node, group, node.id)}</Box>
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );

  const renderRadioButtonGroup = ({ group, values }: Filters) => (
    <Stack spacing={4}>
      {values.map(({ id, value }) => (
        <Box key={value} sx={{ display: 'flex', alignItems: 'center' }}>
          <Radio
            size="small"
            value={value}
            onChange={() => setFilter({ id, name: value, group })}
            checked={filterExists(id, group)}
          />
          <Box component="label" htmlFor={value} sx={{ cursor: 'pointer' }}>
            {value}
          </Box>
        </Box>
      ))}
    </Stack>
  );

  const renderOptions = (options?: Option[]) =>
    options?.length ? (
      <Box
        sx={{
          display: { xs: 'block', md: 'flex' },
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        {options.map((option) => (
          <Box key={option.value} sx={{ display: 'flex', alignItems: 'center' }}>
            <Radio
              size="small"
              value={option.value}
              onChange={() => useSolutionStore.setState({ industry: option.value })}
              checked={industry === option.value}
            />
            <Box component="label" htmlFor={option.value} sx={{ cursor: 'pointer' }}>
              {capitalizeFirstLetter(option.value)}
            </Box>
          </Box>
        ))}
      </Box>
    ) : null;

  const renderFilterFooter = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          p: 4,
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'space-between',
            width: '100%',
            '& > *': { flexGrow: 1, display: 'flex', justifyContent: 'center' },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography>Showing {filteredProducts.length}</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<DeleteSweep />}
            disabled={!filters.length}
            onClick={() => clearFilters()}
            size="small"
          >
            Clear filters
          </Button>
        </Box>
      </Box>
    ),
    [filteredProducts.length, filters.length],
  );

  const renderChildren = (filterValue: Filters) =>
    filterValue.children ? renderCheckboxGroup(filterValue) : renderCheckboxes(filterValue);

  const onChangeShowType = (value: ShowType[]) => {
    useSolutionStore.setState({ showType: value.length ? value : ['Show Solutions'] });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isLoggedIn && <ShowTypeSelection onChange={onChangeShowType} showType={showType} />}
        {filtersValue
          .filter((f) => f.values.length || f.children?.length)
          .map((filterValue) => (
            <Box key={filterValue.label} sx={{ backgroundColor: 'background.paper' }}>
              <Accordion disableGutters defaultExpanded={filterValue.group == FilterGroup.USER_PERSONA}>
                <AccordionSummary
                  sx={{
                    backgroundColor: 'background.paper',
                    boxShadow: 'none',
                    border: 'none',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="cardHeading">{filterValue.label}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {renderOptions(filterValue.options)}
                    {filterValue.singleSelect ? renderRadioButtonGroup(filterValue) : renderChildren(filterValue)}
                    {filterValue.group === FilterGroup.PORTFOLIO && (
                      <Tooltip
                        title={
                          Object.values(filters).some((f) => f.group === FilterGroup.PORTFOLIO)
                            ? 'Export Saleable Products to PDF'
                            : 'Select at least one portfolio to enable export'
                        }
                      >
                        <Typography component="span">
                          <LoadingButton
                            loading={isLoadingExport}
                            icon={<PictureAsPdfOutlined />}
                            variant="outlined"
                            sx={{ mt: 2 }}
                            disabled={!Object.values(filters).some((f) => f.group === FilterGroup.PORTFOLIO)}
                            onClick={downloadSaleableProducts}
                            type="button"
                          >
                            Export Saleable Products
                          </LoadingButton>
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}
      </Box>
      <Box>{renderFilterFooter}</Box>
    </Box>
  );
};

export default ProductFilter;
